import { useCallback, useMemo, useState } from 'react';
import { Chip, Stack } from '@mui/material';
import { debounce } from 'lodash';
import { InputTypeahead } from '@farmshare/ui-components';
import { useAdminUserSearchLazyQuery } from '../lib/graphql';

interface UserOption {
  _id: string;
  fullName: string;
  email?: string;
  phone?: string;
  company?: string;
  role?: string;
  first_name?: string;
  last_name?: string;
  active_vendor?: {
    shop_name: string;
  };
  relationships?: Array<{
    type?: string;
    vendorId?: string;
    vendor?: {
      shop_name: string;
    };
  }>;
}

interface AdminUserSearchProps {
  nameOverride?: string;
  label?: string;
  autoFocus?: boolean;
}

function UserDescription({ opt }: { opt: UserOption }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
      {opt.role && (
        <Chip
          label={opt.role}
          size="small"
          color="primary"
          sx={{ textTransform: 'capitalize' }}
        />
      )}
      {opt.email && <Chip label={opt.email} size="small" />}
      {opt.phone && <Chip label={opt.phone} size="small" />}
      {opt.active_vendor?.shop_name && (
        <Chip
          label={`Vendor: ${opt.active_vendor.shop_name}`}
          size="small"
          color="secondary"
          sx={{ textTransform: 'capitalize' }}
        />
      )}
      {!!opt.relationships?.length &&
        opt.relationships.map((rel, idx) => {
          const type = rel.type || 'N/A';
          const vendorName = rel.vendor?.shop_name || 'Unknown Vendor';
          const key = `${rel.vendorId || idx}-${type}`;
          return (
            <Chip
              key={key}
              label={`${type} at ${vendorName}`}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          );
        })}
    </Stack>
  );
}

export function AdminUserSearch({
  nameOverride = 'user',
  label = 'Search Users',
  autoFocus = false,
}: AdminUserSearchProps) {
  const [searchUsers] = useAdminUserSearchLazyQuery();
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  const renderDescription = useCallback(
    (opt: unknown) => <UserDescription opt={opt as UserOption} />,
    [],
  );

  const performSearch = useCallback(
    async (searchValue: string) => {
      if (!searchValue || searchValue.length < 2) {
        setUserOptions([]);
        return;
      }

      try {
        const { data } = await searchUsers({
          variables: { search: searchValue, limit: 10 },
          fetchPolicy: 'no-cache',
        });

        const results = data?.adminUserSearch || [];
        const options: UserOption[] = results.map((user) => {
          const name =
            `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
          const fullName = user.company
            ? `${user.company} (${name || user.email || user._id})`
            : name || user.email || user._id;

          return {
            _id: String(user._id),
            fullName,
            email: user.email ?? undefined,
            phone: user.phone ?? undefined,
            company: user.company ?? undefined,
            role: user.role ?? undefined,
            first_name: user.first_name ?? undefined,
            last_name: user.last_name ?? undefined,
            active_vendor: user.active_vendor
              ? { shop_name: user.active_vendor.shop_name }
              : undefined,
            relationships: user.relationships?.map((rel) => ({
              type: rel?.type ?? undefined,
              vendorId: rel?.vendor?._id ? String(rel.vendor._id) : undefined,
              vendor: rel?.vendor
                ? { shop_name: rel.vendor.shop_name }
                : undefined,
            })),
          };
        });

        setUserOptions(options);
      } catch (_error) {
        setUserOptions([]);
      }
    },
    [searchUsers],
  );

  const debouncedSearch = useMemo(
    () => debounce(performSearch, 300),
    [performSearch],
  );

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  return (
    <InputTypeahead
      label={label}
      options={userOptions}
      idKey="_id"
      labelKey="fullName"
      size="small"
      nameOverride={nameOverride}
      onInputChange={handleSearch}
      getOptionDescription={renderDescription}
      autoFocus={autoFocus}
    />
  );
}
